import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Route } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { map } from 'rxjs';
import { CanDeactivateMessageCenterGuard } from './guards/can-deactivate-message-center.guard';
import { rostersGuard } from './guards/rosters.guard';
import { selectSchoolGuard } from './guards/select-school.guard';
import { appRoleGuard, enrollmentGuard, newUserEnrollmentRedirect } from '@k12/app-services';
import {
  ClosedEnrollmentComponent,
  ENROLLMENT_READINESS,
  EnrollmentReadiness,
  SignOutComponent
} from '@k12/app-components';
import { environment } from '../environments/environment';
import { SchoolsService } from './services/schools.service';
import { UnauthorizedComponent } from './unauthorized/unauthorized.component';

// A registrant holding only this has no school record yet, so the dashboard would be empty for
// them and they belong in the registration wizard.
const SCHOOL_NEW_ROLES = ['K12.School.New'];

export const appRoutes: Route[] = [
  {
    path: 'unauthorized',
    component: UnauthorizedComponent
  },
  {
    // Public: MSAL returns here after logout. Behind MsalGuard this would immediately start a new login.
    path: 'sign-out',
    component: SignOutComponent
  },
  {
    path: '',
    canActivate: [
      MsalGuard,
      appRoleGuard(
        [
          'K12.School.Admin.Approved',
          'K12.School.Admin.Pending',
          'K12.School.User.Approved',
          'K12.School.User.Pending',
          'K12.Institution.Admin.Approved',
          'K12.Institution.User.Approved',
          'K12.School.New'
        ],
        [environment.registrantGroup]
      )
    ],
    children: [
      {
        path: 'enrollment',
        canActivate: [enrollmentGuard('School')],
        loadComponent: () => import('./enrollment/enrollment.component').then((m) => m.EnrollmentComponent)
      },
      {
        path: 'closed',
        component: ClosedEnrollmentComponent
      },
      {
        // Post-submit landing for the School Application: polls until the async saga has created the
        // institution, then forwards to the dashboard.
        path: 'enrollment-complete',
        providers: [
          {
            provide: ENROLLMENT_READINESS,
            useFactory: (): EnrollmentReadiness => {
              // Deliberately not SchoolsService.loadSchools(): it drives the global spinner and
              // raises an error toast on every failure, which across a 30-attempt poll would flash
              // the spinner and stack thirty toasts while provisioning is merely still running.
              const http = inject(HttpClient);
              const schools = inject(SchoolsService);
              return {
                recordNoun: 'school',
                programType: 'School',
                isReady: () =>
                  http
                    .get<{ entity?: { institutions?: unknown[] } }>(
                      `${environment.adminApiUrl}/schools/GetInstitutions`
                    )
                    .pipe(map((res) => (res?.entity?.institutions?.length ?? 0) > 0)),
                // The spinner and error toast that rule loadSchools out of the poll above are fine
                // here: this fires once, after a read that just succeeded. AppComponent is already
                // subscribed to SchoolsService.schools, which has held an empty list since bootstrap
                // because the registrant had no institution then.
                onProvisioned: () => schools.loadSchools()
              };
            }
          }
        ],
        loadComponent: () => import('@k12/app-components').then((m) => m.EnrollmentCompleteComponent)
      },
      {
        path: '',
        canActivate: [newUserEnrollmentRedirect('School', SCHOOL_NEW_ROLES)],
        loadComponent: () => import('./dashboard/dashboard.component').then((m) => m.DashboardComponent)
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./dashboard/dashboard.component').then((m) => m.DashboardComponent)
      },
      {
        path: 'home',
        loadComponent: () => import('./dashboard/dashboard.component').then((m) => m.DashboardComponent)
      },
      { path: 'login', loadComponent: () => import('./login/login.component').then((m) => m.LoginComponent) },
      { path: 'profile', loadComponent: () => import('./profile/profile.component').then((m) => m.ProfileComponent) },
      {
        path: 'documents',
        loadComponent: () => import('./documents/documents.component').then((m) => m.SchoolDocumentsComponent)
      },
      { path: 'profile', loadComponent: () => import('./profile/profile.component').then((m) => m.ProfileComponent) },
      { path: 'mypage', loadComponent: () => import('./school/school.component').then((m) => m.SchoolComponent) },
      {
        path: 'select-school/:programPartnerId',
        canActivate: [selectSchoolGuard],
        children: []
      },
      {
        path: 'messages',
        loadComponent: () => import('./message-center/message-center.component').then((m) => m.MessageCenterComponent),
        canDeactivate: [CanDeactivateMessageCenterGuard]
      },
      {
        path: 'tasks/:id/upload/:typeId',
        loadComponent: () => import('./documents/upload/upload.component').then((m) => m.UploadComponent)
      },
      {
        path: 'tasks/:id/confirmation/:typeId',
        loadComponent: () => import('./confirmation/confirmation.component').then((m) => m.ConfirmationComponent)
      },
      {
        path: 'tasks/:id/task-link/:typeId',
        loadComponent: () => import('./task-link/task-link.component').then((m) => m.TaskLinkComponent)
      },
      {
        path: 'tasks/:id/pandadoc/:typeId',
        loadComponent: () => import('./task-pandadoc/task-pandadoc.component').then((m) => m.TaskPandaDocComponent)
      },
      {
        path: 'tasks/:id/form/:typeId',
        loadComponent: () => import('./task-form/task-form.component').then((m) => m.TaskFormComponent)
      },
      // {
      //   path: 'tasks/forms/withdrawal',
      //   loadComponent: () => import('./task-form/task-form-withdrawal.component').then((m) => m.TaskFormWithdrawalComponent)
      // },
      {
        path: 'rosters',
        canActivate: [rostersGuard],
        loadComponent: () => import('./rosters/rosters.component').then((m) => m.RostersComponent)
      },
      {
        path: 'reports',
        loadComponent: () => import('./reports/reports.component').then((m) => m.ReportsComponent)
      },
      {
        path: 'reports/pending-awards',
        loadComponent: () => import('./reports/pending-awards.component').then((m) => m.PendingAwardsComponent)
      },
      {
        path: 'reports/new-student-applications',
        loadComponent: () =>
          import('./reports/new-student-applications.component').then((m) => m.NewStudentApplicationsComponent)
      },
      {
        path: 'reports/refunds-due',
        loadComponent: () => import('./reports/refunds-due.component').then((m) => m.RefundsDueComponent)
      },
      {
        path: 'reports/award-payment-student-detail',
        loadComponent: () =>
          import('./reports/award-payment-student-detail.component').then((m) => m.AwardPaymentStudentDetailComponent)
      },
      {
        path: 'reports/end-of-year-payment-summary',
        loadComponent: () =>
          import('./reports/end-of-year-payment-summary.component').then((m) => m.EndOfYearPaymentSummaryComponent)
      }
    ]
  }
];
